<template>
  <!-- Teleport na body, z-50 - Tailwind 2 bez JIT nie generuje wartości dowolnych. -->
  <Teleport to="body">
  <div class="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="subiekt-trasy-title">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" @click="$emit('close')"></div>

    <div class="relative flex subiekt-trasy-panel w-full max-w-7xl flex-col overflow-hidden rounded-t-xl bg-gray-50 shadow-xl sm:rounded-xl">

      <!-- NAGŁÓWEK -->
      <div class="flex items-start gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <span class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 font-mono text-sm font-bold text-blue-700">WZ</span>
        <div class="min-w-0 flex-1">
          <h3 id="subiekt-trasy-title" class="text-base font-semibold text-gray-900">Utwórz wydania z tras</h3>
          <p class="mt-0.5 text-sm text-gray-500">Zaznacz trasy. Z ich zamówień powstanie po jednym wydaniu na kontrahenta.</p>
        </div>
        <button @click="$emit('close')" type="button"
          class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-700">
          <span class="sr-only">Zamknij</span>
          <XIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div class="flex min-h-0 flex-1 flex-col lg:flex-row">

        <!-- LEWA: wybór tras -->
        <div class="flex min-h-0 flex-1 flex-col bg-white lg:border-r lg:border-gray-200">
          <!-- Zakres dat i wyszukiwarka - ten sam styl pól co w panelu. -->
          <div class="flex flex-wrap items-end gap-3 border-b border-gray-200 px-6 pb-3 pt-5">
            <div class="relative rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
              <label class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Trasy od</label>
              <input v-model="dateFrom" @change="load()" type="date" class="block h-9 border-0 bg-transparent px-3 text-sm focus:ring-0" />
            </div>
            <div class="relative rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
              <label class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">do</label>
              <input v-model="dateTo" @change="load()" type="date" class="block h-9 border-0 bg-transparent px-3 text-sm focus:ring-0" />
            </div>
            <div class="inline-flex h-9 overflow-hidden rounded-md border border-gray-300 shadow-sm">
              <button v-for="(shortcut, index) in rangeShortcuts" :key="shortcut.etykieta" @click="applyRange(shortcut)" type="button"
                :class="[isActiveRange(shortcut) ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
                         index > 0 ? 'border-l border-gray-300' : '', 'px-3 text-xs font-semibold transition duration-200']">
                {{ shortcut.etykieta }}
              </button>
            </div>
            <div class="relative min-w-0 flex-1">
              <SearchIcon class="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
              <input v-model="query" type="search" placeholder="Szukaj: miasto, numer trasy, kierowca, kanał…"
                class="block h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-8 text-sm placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-6 py-2 text-xs">
            <span class="text-gray-500">
              <template v-if="loading">Wczytuję trasy…</template>
              <template v-else>
                <span class="font-semibold text-gray-700">{{ query ? `${visibleRoutes.length} z ${routes.length}` : routes.length }}</span> tras
                <span v-if="routes.length > freeRoutes.length" class="text-gray-400"> · {{ routes.length - freeRoutes.length }} już na dokumentach</span>
                <span v-if="selectedOutsideFilter > 0" class="text-indigo-600"> · {{ selectedOutsideFilter }} zaznaczonych poza filtrem</span>
              </template>
            </span>
            <button v-if="!loading && freeVisible.length > 0" @click="toggleVisible()" type="button"
              class="font-semibold text-indigo-600 transition duration-200 hover:text-indigo-500">
              {{ allSelected ? (query ? 'Odznacz widoczne' : 'Odznacz wszystkie') : (query ? `Zaznacz widoczne (${freeVisible.length})` : 'Zaznacz wszystkie') }}
            </button>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto">
            <div v-if="loading" class="divide-y divide-gray-100">
              <div v-for="n in 7" :key="n" class="flex items-center gap-4 px-6 py-3.5">
                <div class="h-4 w-4 flex-shrink-0 animate-pulse rounded bg-gray-200"></div>
                <div class="min-w-0 flex-1">
                  <div class="h-3.5 animate-pulse rounded-full bg-gray-200" :style="{ width: szerokoscSzkieletu(n) }"></div>
                  <div class="mt-2 h-3 w-32 animate-pulse rounded-full bg-gray-100"></div>
                </div>
                <div class="h-5 w-20 flex-shrink-0 animate-pulse rounded bg-gray-100"></div>
              </div>
            </div>

            <div v-else-if="loadError" class="flex flex-wrap items-center gap-x-3 gap-y-1 px-6 py-6">
              <p class="flex items-center gap-1.5 text-sm text-red-700">
                <ExclamationIcon class="h-4 w-4 flex-shrink-0" aria-hidden="true" />{{ loadError }}
              </p>
              <button @click="load()" type="button" class="text-xs font-semibold text-indigo-600 hover:text-indigo-500">Spróbuj ponownie</button>
            </div>

            <div v-else-if="routes.length === 0" class="px-6 py-14 text-center">
              <TruckIcon class="mx-auto h-9 w-9 text-gray-300" aria-hidden="true" />
              <p class="mt-2 text-sm font-semibold text-gray-900">Brak tras w tym zakresie</p>
              <p class="mt-1 text-sm text-gray-500">Zmień daty albo wybierz „ten tydzień”.</p>
            </div>

            <p v-else-if="visibleRoutes.length === 0" class="px-6 py-8 text-center text-sm text-gray-500">
              Żadna trasa nie pasuje do „{{ query }}”.
              <button @click="query = ''" type="button" class="ml-1 font-semibold text-indigo-600 hover:text-indigo-500">Wyczyść</button>
            </p>

            <!-- Trasy pogrupowane po dniu startu - przy zakresie tygodnia to dzień,
                 a nie nazwa, odróżnia dwa przejazdy. -->
            <div v-else>
              <div v-for="group in routesByDay" :key="group.day">
                <div class="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-2">
                  <span class="text-xs font-semibold uppercase text-gray-600">
                    {{ longDate(group.day) }}
                    <span class="ml-1 font-normal normal-case text-gray-400">· {{ group.routes.length }} {{ group.routes.length === 1 ? 'trasa' : 'tras' }}</span>
                  </span>
                  <button v-if="group.free.length > 0" @click="toggleDay(group)" type="button" class="text-xs font-semibold text-indigo-600 hover:text-indigo-500">
                    {{ group.free.every(r => selectedIds.includes(r.id)) ? 'Odznacz dzień' : 'Zaznacz dzień' }}
                  </button>
                </div>
                <!-- Trasa wpięta w istniejący dokument jest zablokowana - wzięta drugi raz
                     zdjęłaby ten sam towar ze stanu dwoma wydaniami. -->
                <label v-for="route in group.routes" :key="route.id"
                  :class="[route.usedIn ? 'cursor-not-allowed bg-gray-50'
                             : (selectedIds.includes(route.id) ? 'cursor-pointer border-indigo-500 bg-indigo-50' : 'cursor-pointer border-transparent hover:bg-gray-50'),
                           'flex items-center gap-4 border-b border-l-4 border-b-gray-100 px-5 py-3 transition duration-150']">
                  <input :value="route.id" v-model="selectedIds" type="checkbox" :disabled="route.usedIn != null"
                    :class="[route.usedIn ? 'cursor-not-allowed opacity-40' : '', 'h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500']" />
                  <span class="min-w-0 flex-1">
                    <span :title="route.title" :class="[route.usedIn ? 'text-gray-400' : 'text-gray-900', 'block truncate text-sm font-semibold']">{{ route.title }}</span>
                    <span v-if="route.usedIn" class="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                      <LockClosedIcon class="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                      <span class="truncate" :title="route.usedIn.title">już na dokumencie: {{ route.usedIn.title }}</span>
                    </span>
                    <span v-else class="mt-0.5 block text-xs text-gray-500">
                      {{ route.orderCount }} {{ route.orderCount === 1 ? 'zamówienie' : 'zamówień' }} · {{ route.itemCount }} {{ route.itemCount === 1 ? 'pozycja' : 'pozycji' }}
                    </span>
                  </span>
                  <!-- Kanały, na które rozejdzie się trasa - to one decydują o liczbie dokumentów. -->
                  <span class="flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5">
                    <span v-for="g in route.groups" :key="g.key" :class="[channelClass(g), 'rounded-md px-2 py-0.5 text-xs font-semibold']">
                      {{ g.name }} <span class="font-normal opacity-70">{{ g.itemCount }}</span>
                    </span>
                    <span v-if="!route.finished" class="ml-1 inline-flex w-24 items-center justify-end gap-1.5 text-xs text-gray-500">
                      <span :class="[route.planned ? 'bg-yellow-400' : 'bg-blue-500', 'h-2 w-2 rounded-full']"></span>{{ route.statusName }}
                    </span>
                    <span v-else class="ml-1 inline-flex w-24 items-center justify-end gap-1.5 text-xs text-gray-400">
                      <span class="h-2 w-2 rounded-full bg-green-500"></span>{{ route.statusName }}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- PRAWA: co powstanie -->
        <div class="flex min-h-0 w-full flex-shrink-0 flex-col border-t border-gray-200 lg:w-96 lg:border-t-0">
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            <!-- Data dokumentu na samej górze - to ona trafia do Subiekta i od niej zależy,
                 czy towar będzie na stanie. Ustawia się sama na dzień zaznaczonych tras. -->
            <div class="rounded-lg border bg-white px-4 py-4 shadow-sm">
              <div class="relative rounded-md border border-gray-300 bg-white focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
                <label class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Data wydania (wyjazd)</label>
                <input v-model="documentDate" @input="dateTouched = true" type="date" class="block h-10 w-full border-0 bg-transparent px-3 text-sm font-semibold focus:ring-0" />
              </div>
              <p v-if="selectedDays.length === 1 && documentDate === selectedDays[0]" class="mt-2 flex items-center gap-1.5 text-xs text-green-700">
                <CheckCircleIcon class="h-4 w-4 flex-shrink-0" aria-hidden="true" />Zgodna z dniem zaznaczonych tras.
              </p>
              <p v-else-if="selectedDays.length > 1" class="mt-2 flex gap-1.5 text-xs text-yellow-800">
                <ExclamationIcon class="h-4 w-4 flex-shrink-0 text-yellow-500" aria-hidden="true" />
                Trasy są z {{ selectedDays.length }} dni ({{ shortDate(selectedDays[0]) }}–{{ shortDate(selectedDays[selectedDays.length - 1]) }}), a wydanie dostanie jedną datę.
              </p>
              <p v-else-if="selectedDays.length === 1" class="mt-2 flex gap-1.5 text-xs text-yellow-800">
                <ExclamationIcon class="h-4 w-4 flex-shrink-0 text-yellow-500" aria-hidden="true" />
                Inna niż dzień tras ({{ shortDate(selectedDays[0]) }}).
                <button @click="documentDate = selectedDays[0]; dateTouched = false" type="button" class="font-semibold underline">Ustaw {{ shortDate(selectedDays[0]) }}</button>
              </p>
            </div>

            <h4 class="mt-5 text-xs font-semibold uppercase text-gray-500">Powstanie</h4>
            <div v-if="selectedIds.length === 0" class="mt-2 rounded-lg border-2 border-dashed border-gray-200 px-4 py-8 text-center">
              <DocumentTextIcon class="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
              <p class="mt-2 text-sm text-gray-500">Zaznacz trasy po lewej — tutaj zobaczysz wydania, które z nich powstaną.</p>
            </div>
            <template v-else>
              <p class="mt-1 text-xs text-gray-500">z {{ selectedIds.length }} {{ selectedIds.length === 1 ? 'trasy' : 'tras' }}</p>
              <ul class="mt-2 space-y-2">
                <li v-for="d in preview.dokumenty" :key="d.key" class="rounded-lg border bg-white px-4 py-3 shadow-sm">
                  <div class="flex items-center justify-between gap-2">
                    <span :title="d.name" class="truncate text-sm font-semibold text-gray-900">{{ d.name }}</span>
                    <span class="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 doc-tabular">{{ d.itemCount }} poz.</span>
                  </div>
                  <p v-if="d.contractorSymbol" class="mt-0.5 truncate font-mono text-xs text-gray-500">{{ d.contractorSymbol }}</p>
                  <p v-if="d.fromInvoice.length > 0" class="mt-1 text-xs text-gray-500">
                    w tym fakturowani: {{ d.fromInvoice.map(f => `${f.name} (${f.itemCount})`).join(', ') }}
                  </p>
                </li>
              </ul>
              <div v-if="preview.zaplanowane.length > 0" class="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2.5">
                <p class="flex gap-2 text-xs font-semibold text-yellow-900">
                  <ExclamationIcon class="h-4 w-4 flex-shrink-0 text-yellow-500" aria-hidden="true" />
                  {{ preview.zaplanowane.length }} {{ preview.zaplanowane.length === 1 ? 'trasa jest dopiero zaplanowana' : 'tras jest dopiero zaplanowanych' }} — mogą dojść do nich zamówienia.
                </p>
              </div>
            </template>
          </div>

          <div class="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-5 py-3">
            <button @click="$emit('close')" type="button"
              class="inline-flex h-9 items-center rounded-md bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition duration-200 hover:bg-gray-50">
              Anuluj
            </button>
            <button @click="create()" :disabled="creating || selectedIds.length === 0 || !documentDate" type="button"
              :class="[(creating || selectedIds.length === 0 || !documentDate) ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500',
                       'inline-flex h-9 items-center gap-x-2 rounded-md bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition duration-200']">
              <RefreshIcon v-if="creating" class="h-4 w-4 animate-spin" aria-hidden="true" />
              <template v-if="creating">Tworzę…</template>
              <template v-else-if="selectedIds.length === 0">Utwórz wydania</template>
              <template v-else>Utwórz {{ preview.dokumenty.length }} {{ documentWord(preview.dokumenty.length) }}</template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import moment from 'moment';
import { XIcon, RefreshIcon, ExclamationIcon, DocumentTextIcon, LockClosedIcon, SearchIcon, CheckCircleIcon, TruckIcon } from '@heroicons/vue/solid';

// Wyszukiwanie ma być odporne na polskie znaki i wielkość liter: „mysiadlo" musi
// znaleźć „Mysiadło". NFD rozkłada ogonki i kreski, ale nie „ł" — tę trzeba osobno.
function normalizuj(tekst) {
  return (tekst || '').toString().toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
import { subiektPost } from '@/subiektApi';

export default {
  name: 'SubiektRouteSelectModal',
  components: { XIcon, RefreshIcon, ExclamationIcon, DocumentTextIcon, LockClosedIcon, SearchIcon, CheckCircleIcon, TruckIcon },
  props: {
    date: { type: String, required: true },
  },
  emits: ['close', 'created'],
  data() {
    return {
      dateFrom: this.date,
      dateTo: this.date,
      documentDate: this.date,
      routes: [],
      selectedIds: [],
      loading: false,
      requestSeq: 0,
      query: '',
      loadError: null,
      creating: false,
      // Czy ktoś sam zmienił datę wydania - wtedy nie nadpisujemy jej dniem tras.
      dateTouched: false,
    };
  },
  computed: {
    // Skróty zakresu liczone od dnia otwartego w panelu, a nie od „dziś" — księgowość
    // składa dokumenty wstecz i zwykle pracuje na dniu, który ma na ekranie.
    rangeShortcuts() {
      const d = moment(this.date, 'YYYY-MM-DD');
      return [
        { etykieta: 'ten dzień', od: d.format('YYYY-MM-DD'), do: d.format('YYYY-MM-DD') },
        { etykieta: 'ten tydzień', od: d.clone().startOf('isoWeek').format('YYYY-MM-DD'), do: d.clone().endOf('isoWeek').format('YYYY-MM-DD') },
        { etykieta: 'ten miesiąc', od: d.clone().startOf('month').format('YYYY-MM-DD'), do: d.clone().endOf('month').format('YYYY-MM-DD') },
      ];
    },
    selectedDays() {
      const selectedIds = new Set(this.selectedIds);
      const days = new Set();
      for(const t of this.routes) if(selectedIds.has(t.id) && t.day) days.add(t.day);
      return [...days].sort();
    },
    // Trasy, które da się jeszcze wziąć. Zajęte nie liczą się ani do „zaznacz
    // wszystkie", ani do sprawdzenia, czy wszystko już zaznaczono — inaczej przycisk
    // nigdy nie zmieniłby się w „odznacz wszystkie".
    freeRoutes() {
      return this.routes.filter(t => !t.usedIn);
    },
    // Filtr działa na tym, co widać w wierszu: tytuł, kontrahenci i status. Szukanie
    // tylko po tytule zawiodłoby przy „pozostałe" albo „zaplanowana", a to naturalne
    // frazy dla kogoś, kto patrzy na tę listę.
    visibleRoutes() {
      const frazy = normalizuj(this.query).split(/\s+/).filter(Boolean);
      if(frazy.length === 0) return this.routes;
      return this.routes.filter(t => {
        const stog = normalizuj([
          t.title,
          t.statusName,
          ...(t.groups || []).map(g => g.name),
          t.usedIn ? t.usedIn.title : '',
        ].join(' '));
        return frazy.every(f => stog.includes(f));
      });
    },
    routesByDay() {
      const groups = [];
      for (const route of this.visibleRoutes) {
        const day = route.day || '';
        let group = groups.find(g => g.day === day);
        if (!group) { group = { day, routes: [], free: [] }; groups.push(group); }
        group.routes.push(route);
        if (!route.usedIn) group.free.push(route);
      }
      return groups;
    },
    freeVisible() {
      return this.visibleRoutes.filter(t => !t.usedIn);
    },
    selectedOutsideFilter() {
      const visibleIds = new Set(this.visibleRoutes.map(t => t.id));
      return this.selectedIds.filter(id => !visibleIds.has(id)).length;
    },
    allSelected() {
      return this.freeVisible.length > 0
        && this.freeVisible.every(t => this.selectedIds.includes(t.id));
    },
    // Co POWSTANIE z zaznaczenia — po jednym dokumencie na kanał, bo kanał wyznacza
    // kontrahenta wydania. Liczby biorą się z tej samej struktury, którą backend zwrócił
    // przy trasach i którą sam dzieli przy składaniu, więc obietnica z ekranu i wynik
    // nie rozjadą się przy pierwszej zmianie po jednej stronie.
    preview() {
      const selectedIds = new Set(this.selectedIds);
      const dokumenty = {};
      const zaplanowane = [];

      for(const route of this.routes) {
        if(!selectedIds.has(route.id)) continue;
        if(route.planned) zaplanowane.push(route.title);

        for(const g of (route.groups || [])) {
          if(dokumenty[g.key] == null) {
            dokumenty[g.key] = {
              key: g.key,
              name: g.name,
              contractorSymbol: g.contractorSymbol,
              itemCount: 0,
              fromInvoice: {},
            };
          }
          const d = dokumenty[g.key];
          d.itemCount += g.itemCount;
          // Kontrahenci fakturowani jadą na dokumencie swojego kanału — sumujemy ich
          // po to, żeby powiedzieć, na KTÓRYM dokumencie i ile ich będzie.
          for(const f of (g.fromInvoice || [])) {
            d.fromInvoice[f.name] = (d.fromInvoice[f.name] || 0) + f.itemCount;
          }
        }
      }

      const items = Object.values(dokumenty)
        .map(d => ({
          ...d,
          fromInvoice: Object.keys(d.fromInvoice)
            .map(labelText => ({ name: labelText, itemCount: d.fromInvoice[labelText] }))
            .sort((a, b) => b.itemCount - a.itemCount),
        }))
        .sort((a, b) => b.itemCount - a.itemCount);

      return {
        dokumenty: items,
        zaplanowane,
      };
    },
  },
  watch: {
    // Data wydania = dzień zaznaczonych tras, dopóki ktoś jej sam nie zmienił.
    selectedDays(days) {
      if (!this.dateTouched && days.length === 1) this.documentDate = days[0];
    },
  },
  methods: {
    longDate(dayKey) {
      return dayKey ? moment(dayKey, 'YYYY-MM-DD').locale('pl').format('dddd, D MMMM YYYY') : 'Bez daty';
    },
    isActiveRange(shortcut) {
      return this.dateFrom === shortcut.od && this.dateTo === shortcut.do;
    },
    toggleDay(group) {
      const ids = group.free.map(r => r.id);
      if (ids.every(id => this.selectedIds.includes(id))) {
        const removed = new Set(ids);
        this.selectedIds = this.selectedIds.filter(id => !removed.has(id));
      } else {
        this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
      }
    },
    // Kolor kanału - ten sam kanał zawsze tym samym kolorem, w liście i w podsumowaniu.
    channelClass(group) {
      const key = (group && (group.key || group.name) || '').toString().toUpperCase();
      if (key.includes('OTTO') || key.includes('LUTZ')) return 'bg-blue-100 text-blue-800';
      if (key.includes('BOL')) return 'bg-orange-100 text-orange-800';
      if (key.includes('FAKTURA')) return 'bg-purple-100 text-purple-800';
      return 'bg-gray-100 text-gray-700';
    },
    // Polska odmiana po liczbie. Pada w dwóch miejscach i rozjechanie się ich
    // („1 dokumentów" na przycisku) czyta się jak błąd systemu.
    documentWord(ile) {
      if(ile === 1) return 'dokument';
      const dziesiatki = ile % 100;
      const jednosci = ile % 10;
      if(jednosci >= 2 && jednosci <= 4 && (dziesiatki < 12 || dziesiatki > 14)) return 'dokumenty';
      return 'dokumentów';
    },
    // Nierówne długości pasków — równe wyglądają jak zepsuty układ, a nie jak
    // ładujące się nazwy tras. Z indeksu, żeby nie migotały przy każdym renderze.
    szerokoscSzkieletu(n) {
      return ['62%', '45%', '73%', '38%', '55%'][(n - 1) % 5];
    },
    // Przełącza tylko to, co widać. Zaznaczenia spoza filtra zostają nietknięte —
    // dzięki temu da się zebrać dokument z kilku wyszukiwań po kolei.
    shortDate(dayKey) {
      return dayKey ? moment(dayKey, 'YYYY-MM-DD').format('DD.MM') : '—';
    },
    applyRange(shortcut) {
      this.dateFrom = shortcut.od;
      this.dateTo = shortcut.do;
      this.load();
    },
    toggleVisible() {
      const visibleIds = this.freeVisible.map(t => t.id);
      if(this.allSelected) {
        const zdejmowane = new Set(visibleIds);
        this.selectedIds = this.selectedIds.filter(id => !zdejmowane.has(id));
      } else {
        this.selectedIds = [...new Set([...this.selectedIds, ...visibleIds])];
      }
    },
    async load() {
      // Pole daty bywa chwilowo puste w trakcie wpisywania. Bez tego backend odbijałby
      // „nieprawidłowa data" przy każdym takim stanie i migał czerwonym komunikatem.
      if(!this.dateFrom || !this.dateTo) return;

      // Numer żądania. Zmiana „od", a zaraz potem „do", zostawia DWA zapytania w locie —
      // a miesięczny zakres wraca wolniej niż jednodniowy. Bez tego starsza odpowiedź
      // potrafi przyjść później i nadpisać nowszą, a spinner gaśnie po pierwszej z nich:
      // lista pokazuje wtedy trasy z zupełnie innego zakresu niż ten w polach.
      const moje = ++this.requestSeq;
      this.loading = true;
      this.loadError = null;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/trasy', {
          dateOd: this.dateFrom,
          dateDo: this.dateTo,
        });
        if(moje !== this.requestSeq) return;
        if(!res.data.success) throw res.data.log || res.data.error;
        this.routes = res.data.routes || [];
        // Zaznaczenia tras, których w nowym zakresie nie ma, nie da się już pokazać
        // ani odznaczyć — a weszłyby na dokument po cichu.
        const availableIds = new Set(this.routes.map(t => t.id));
        this.selectedIds = this.selectedIds.filter(id => availableIds.has(id));
      } catch (error) {
        if(moje !== this.requestSeq) return;
        console.log(error);
        this.routes = [];
        this.loadError = error?.response?.status === 404
          ? 'Serwer nie zna tego adresu — panel jest nowszy niż wgrany backend.'
          : (typeof error === 'string' ? error : 'Nie udało się wczytać tras.');
      } finally {
        if(moje === this.requestSeq) this.loading = false;
      }
    },
    async create() {
      if(this.creating || this.selectedIds.length === 0) return;
      // Liczba z podglądu, zdjęta PRZED żądaniem: odpowiedź zawiera wszystkie dokumenty
      // dnia, więc liczenie ich po fakcie doliczyłoby te ze wcześniejszych złożeń.
      const ile = this.preview.dokumenty.length;
      this.creating = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/documents', {
          date: this.documentDate,
          routes: this.selectedIds,
        });
        if(!res.data.success) throw res.data.log || res.data.error;

        this.$emit('created', {
          dokumenty: res.data.dokumenty || [],
          wysylkaWlaczona: res.data.wysylkaWlaczona === true,
          magazyny: res.data.magazyny || {},
          pominiete: res.data.pominiete || null,
        }, this.documentDate);
        this.$store.commit('setNotification', {
          show: true,
          head: `Utworzono ${ile} ${this.documentWord(ile)}.`,
          subheader: '', success: true,
        });
        this.$emit('close');
      } catch (error) {
        console.log(error);
        this.$store.commit('setNotification', {
          show: true, head: 'Nie udało się utworzyć dokumentów.',
          subheader: typeof error === 'string' ? error : 'Coś poszło nie tak.', success: false,
        });
      } finally {
        this.creating = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
/* Wysokość w jednostkach viewportu — Tailwind 2 bez JIT nie ma max-h-[85vh]. */
.subiekt-trasy-panel {
  max-height: 88vh;
  min-height: 24rem;
}

.doc-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
